import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getAPIURL } from "helpers";
import { getBaseQueryWithRejection } from "redux/helper.redux";

const baseQueryWithRejection = getBaseQueryWithRejection(
    fetchBaseQuery({
        baseUrl: getAPIURL("/api/pvsa/dform"),
        prepareHeaders: (headers, { getState }) => {
            const token = getState().auth.accessToken;
            if (token) {
                headers.set("Authorization", `Bearer ${token}`);
            }
            return headers;
        },
    })
);

export const dFormApi = createApi({
    reducerPath: "dFormApi",
    baseQuery: baseQueryWithRejection,
    tagTypes: ["FormMissionCC", "SingleKeyCCData", "CCLOAChange", "ExpertsFromMovement"],
    endpoints: (builder) => ({
        getCCFromTemplate: builder.query({
            query: ({ formId, languageId, missionId }) =>
                `/get-cc-from-template/${formId}?language_id=${languageId}&mission_id=${missionId}`,
            transformResponse: (response) => response.data,
            providesTags: [],
        }),
        submitDForm: builder.mutation({
            query: ({ templateId, movementId, typ, isValidated, userId, skip = '', submission_for = '', dform_assigned_for = '', trigger_assignment = '', userAction = '' }) => ({
                url: `/submit/${templateId}/${movementId}?type=${typ}&is_validated=${isValidated}&user_id=${userId}&skip=${skip}&submission_for=${submission_for}&trigger_assignment=${trigger_assignment}&dform_assigned_for=${dform_assigned_for}&user_action=${userAction}`,
                method: "POST",
                transformResponse: (response) => response.data,
            }),
            // query: ({ templateId, movementId, ...params }) => {
            //     const tmp = new URLSearchParams();
            //     tmp.append('type', ![undefined, null].includes(params.typ) ? params.typ : "");
            //     tmp.append('is_validated', ![undefined, null].includes(params.isValidated) ? params.isValidated : "");
            //     tmp.append('user_id', ![undefined, null].includes(params.userId) ? params.userId : "");
            //     tmp.append('skip', ![undefined, null].includes(params.skip) ? params.skip : "");
            //     tmp.append('submission_for', ![undefined, null].includes(params.submission_for) ? params.submission_for : "");
            //     tmp.append('dform_assigned_for', ![undefined, null].includes(params.dform_assigned_for) ? params.dform_assigned_for : "");
            //     tmp.append('trigger_assignment', ![undefined, null].includes(params.trigger_assignment) ? params.trigger_assignment : "");
            //     tmp.append('user_action', ![undefined, null].includes(params.userAction) ? params.userAction : "");
            //     return {
            //         url: `/submit/${templateId}/${movementId}?${tmp.toString()}`,
            //         method: "POST",
            //         transformResponse: (response) => response.data,
            //     };
            // }
        }),
        getFormMissionCC: builder.query({
            query: ({ formId, missionId }) => `/form-mission-cc/${formId}?mission_id=${missionId}`,
            transformResponse: (response) => response.data,
            providesTags: ["FormMissionCC"],
        }),
        updateFormMissionCC: builder.mutation({
            query: ({ formId, missionId, userId }) => ({
                url: `/form-mission-cc/${formId}`,
                method: "POST",
                body: {
                    mission_id: missionId,
                    user_id: userId,
                },
                transformResponse: (response) => response.data,
            }),
            invalidatesTags: ["FormMissionCC"],
        }),
        getExpertCCData: builder.query({
            query: ({ formId, elementId, movementId }) =>
                `/expert-cc-data/?template_id=${formId}&element_id=${elementId}&movement_id=${movementId}`,
            transformResponse: (response) => response.data,
            providesTags: [],
        }),
        updateExpertCCData: builder.mutation({
            query: (obj) => ({
                url: `/expert-cc-data`,
                method: "POST",
                body: obj,
                transformResponse: (response) => response.data,
            }),
        }),
        getCCData: builder.query({
            query: (params) => {
                const tmp = new URLSearchParams();
                tmp.append('template_id', params.formId);
                if (params.elementId) {
                    tmp.append('element_id', params.elementId);
                }
                tmp.append('movement_id', params.movementId);
                if (params.key) {
                    tmp.append('key', params.key);
                }
                return `/cc-data/?${tmp.toString()}`;
            },
            transformResponse: (response) => response.data,
            providesTags: (result, error, params) => {
                if (params.tagName) return [params.tagName];
                return [];
            }
        }),
        getSingleCCData: builder.query({
            query: (params) => {
                const tmp = new URLSearchParams();
                tmp.append('template_id', params.formId);
                if (params.elementId) {
                    tmp.append('element_id', params.elementId);
                }
                tmp.append('movement_id', params.movementId);
                tmp.append('cc_id', params.ccId);
                if (params.key) {
                    tmp.append('key', params.key);
                }
                return `/cc-data/?${tmp.toString()}`;
            },
            transformResponse: (response) => response.data,
            providesTags: ["SingleKeyCCData"]
        }),
        updateCCData: builder.mutation({
            query: (obj) => ({
                url: `/cc-data`,
                method: "POST",
                body: obj,
                transformResponse: (response) => response.data,
            }),
            invalidatesTags: ["SingleKeyCCData", "CCLOAChange"]
        }),
        deleteCCData: builder.mutation({
            query: (id) => ({
                url: `/cc-data/${id}`,
                method: "DELETE",
                transformResponse: (response) => response.data,
            }),
            invalidatesTags: ["SingleKeyCCData"]
        }),
        getExpertsFromMovement: builder.query({
            query: (obj) => {
                const tmp = new URLSearchParams();
                if (obj?.params?.selectExpertPeereviewer) {
                    tmp.append('select_expert_peereviewer', obj.params.selectExpertPeereviewer);
                }
                return `/get-experts-from-movement/${obj.movementId}?${tmp.toString()}`;
            },
            transformResponse: (response) => response.data,
            providesTags: ["ExpertsFromMovement"]
        }),
    }),
});

export const {
    useSubmitDFormMutation,
    useGetFormMissionCCQuery,
    useUpdateFormMissionCCMutation,
    useGetCCFromTemplateQuery,
    useLazyGetExpertCCDataQuery,
    useUpdateExpertCCDataMutation,
    useGetCCDataQuery,
    useGetSingleCCDataQuery,
    useUpdateCCDataMutation,
    useDeleteCCDataMutation,
    useGetExpertsFromMovementQuery,
    useLazyGetExpertsFromMovementQuery
} = dFormApi;
